import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";
import type { ThreeWayBasis } from "../../../../components/PortfolioPerformance/basis-toggle";

// Cumulative charged-off contracts by months on book, one column per funded-year cohort. A cohort's
// column stops at the last month it has reached, so later months simply have no value for it.
export interface MonthsOnBookRow {
  monthsOnBook: number;
  [cohort: string]: number;
}

// The six cards come from one request (the Realized / Projected / Active toggle moves Net Loss and Default Rate); the two curves are
// their own, so a slow chart never holds the cards back. A card with nothing behind it arrives as an em dash.
export interface ChargeoffAccelerationStats {
  netInvestment: string;
  netLoss: string;
  defaultRate: string;
  unearnedInterest: string;
  earnedInterest: string;
  contractChargeoffs: string;
}

export interface ChargeoffCurves {
  industryCurve: MonthsOnBookRow[];
  portfolioCurve: MonthsOnBookRow[];
}

export function useChargeoffStats(basis: ThreeWayBasis) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["chargeoff-acceleration", "stats", basis, ticket ?? "all"],
    queryFn: () => apiGet<ChargeoffAccelerationStats>("/chargeoff-acceleration", { ticket, basis }),
    // the other cards keep their value while the toggle's figures reload
    placeholderData: keepPreviousData,
  });
}

// Both filters must trigger a refetch, not just redraw from already-fetched data.
export function useChargeoffCurves(basis: ThreeWayBasis, assetIndustry?: string, borrowerIndustry?: string | null) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["chargeoff-acceleration", "curves", basis, assetIndustry ?? null, borrowerIndustry ?? null, ticket ?? "all"],
    queryFn: () =>
      apiGet<ChargeoffCurves>("/chargeoff-acceleration/curves", {
        ticket,
        basis,
        assetIndustry,
        borrowerIndustry: borrowerIndustry ?? undefined,
      }),
    // The industry filters live inside the chart panels, so keep the current curve on screen
    // while a new industry loads instead of blanking the panel (and its filters) out.
    placeholderData: keepPreviousData,
  });
}
